import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Save, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminServices = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const emptyService = {
    title: '',
    subtitle: '',
    description: '',
    icon: 'Refrigerator',
    items: [],
    features: [],
    order: 0,
    is_active: true
  };
  
  const [formData, setFormData] = useState(emptyService);
  const [itemsText, setItemsText] = useState('');
  const [featuresText, setFeaturesText] = useState('');

  const iconOptions = ['Refrigerator', 'Tv', 'Smartphone', 'Wind', 'WashingMachine', 'Microwave', 'Monitor'];

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Hizmetler yüklenemedi');
    }
    setLoading(false);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData(service);
    setItemsText(service.items?.join('\n') || '');
    setFeaturesText(service.features?.join('\n') || '');
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingService(null);
    setFormData({ ...emptyService, order: services.length });
    setItemsText('');
    setFeaturesText('');
    setIsDialogOpen(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Bu hizmeti silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Hizmet silindi');
      fetchServices();
    } catch (error) {
      toast.error('Hizmet silinemedi');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const data = {
      ...formData,
      items: itemsText.split('\n').filter(item => item.trim()),
      features: featuresText.split('\n').filter(item => item.trim())
    };

    try {
      if (editingService) {
        await axios.put(`${API_URL}/api/services/${editingService.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Hizmet güncellendi');
      } else {
        await axios.post(`${API_URL}/api/services`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Hizmet oluşturuldu');
      }
      setIsDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-services">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Hizmetler</h1>
          <p className="text-slate-600">Hizmetlerinizi ekleyin ve düzenleyin.</p>
        </div>
        <Button onClick={handleAdd} className="bg-[#002FA7] hover:bg-[#001d6e]" data-testid="add-service-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Hizmet
        </Button>
      </div>

      <div className="space-y-4">
        {services.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-600">
              Henüz hizmet eklenmemiş. Yeni hizmet eklemek için yukarıdaki butonu kullanın.
            </CardContent>
          </Card>
        ) : (
          services.map((service, index) => (
            <Card key={service.id} data-testid={`service-item-${index}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <GripVertical className="w-5 h-5 text-slate-400 cursor-move" />
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{service.title}</h3>
                  <p className="text-sm text-slate-600">{service.subtitle}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(service)}
                    data-testid={`edit-service-${index}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(service.id)}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`delete-service-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Hizmet Düzenle' : 'Yeni Hizmet'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Başlık</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="service-title-input"
                />
              </div>
              <div>
                <Label htmlFor="subtitle">Alt Başlık</Label>
                <Input
                  id="subtitle"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  data-testid="service-subtitle-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                required
                data-testid="service-description-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="icon">İkon</Label>
                <select
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full border rounded-md p-2"
                  data-testid="service-icon-select"
                >
                  {iconOptions.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="order">Sıra</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  data-testid="service-order-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="items">Ürünler (Her satıra bir tane)</Label>
              <Textarea
                id="items"
                value={itemsText}
                onChange={(e) => setItemsText(e.target.value)}
                rows={4}
                placeholder="Buzdolabı&#10;Çamaşır Makinesi&#10;Bulaşık Makinesi"
                data-testid="service-items-input"
              />
            </div>

            <div>
              <Label htmlFor="features">Özellikler (Her satıra bir tane)</Label>
              <Textarea
                id="features"
                value={featuresText}
                onChange={(e) => setFeaturesText(e.target.value)}
                rows={4}
                placeholder="Ücretsiz ekspertiz&#10;Aynı gün hizmet"
                data-testid="service-features-input"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#001d6e]" data-testid="save-service-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServices;
